'use client';

/**
 * "Ask grandma for her voice" — the inviter's half of a voice invite.
 *
 * Deliberately small: a name for the voice, a link, and the message to send
 * with it. The parent's job here is to press copy and paste it into whatever
 * chat they already use; writing the request for them is most of the work.
 *
 * Nothing is charged until a recording arrives, so a link left unused costs the
 * family nothing and simply expires.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Copy, Check, Mic, X } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Card } from '@/shared/components/ui/card';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import {
  cancelVoiceInvite,
  createVoiceInvite,
  listVoiceInvites,
  type VoiceInviteView,
} from '@/contexts/ai/actions/voiceInvites';
import { publicOrigin } from '@/shared/utils/publicOrigin';

export function VoiceInvitePanel({ locale }: { locale: string }) {
  const t = useTranslations('voiceInvite');
  const [invites, setInvites] = useState<VoiceInviteView[]>([]);
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  /**
   * Asking is a decision, not the default state.
   *
   * The panel rendered the name field and the "ask" button open on the voices
   * screen, so a parent who came to record their OWN voice was met by a form
   * asking them to send a link to grandma — it read as the screen's request
   * rather than as an option. The form opens on the press now; a pending
   * invite stays visible either way, because that one is real state.
   */
  const [asking, setAsking] = useState(false);

  const refresh = useCallback(async () => {
    const res = await listVoiceInvites();
    if (res.success) setInvites(res.data);
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const linkFor = (token: string) =>
    `${publicOrigin()}/${locale}/voice/${token}`;

  const create = async () => {
    setBusy(true);
    setError(null);
    const res = await createVoiceInvite({ label, language: locale });
    setBusy(false);
    if (!res.success) {
      setError(res.error === 'too_many' ? t('owner_tooMany') : res.error);
      return;
    }
    setLabel('');
    setAsking(false);
    await refresh();
  };

  const copy = async (invite: VoiceInviteView) => {
    const text = t('owner_message', { label: invite.label, url: linkFor(invite.token) });
    try {
      await navigator.clipboard.writeText(text);
      setCopied(invite.token);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      setError(t('owner_copy'));
    }
  };

  const cancel = async (token: string) => {
    await cancelVoiceInvite(token);
    await refresh();
  };

  return (
    <Card className="mb-4 space-y-3 p-4" data-testid="voice-invite-panel">
      <div className="flex items-start gap-3">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Mic className="size-5" />
        </div>
        <div className="min-w-0">
          <p className="font-medium">{t('owner_ask')}</p>
          <p className="text-sm text-muted-foreground">{t('owner_askHint')}</p>
        </div>
      </div>

      {!asking ? (
        <Button
          variant="outline"
          onClick={() => setAsking(true)}
          data-testid="voice-invite-open"
          className="w-full sm:w-auto"
        >
          {t('owner_ask')}
        </Button>
      ) : (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <div className="flex-1 space-y-1">
            <Label htmlFor="voice-invite-label" className="text-xs text-muted-foreground">
              {t('owner_labelLabel')}
            </Label>
            <Input
              id="voice-invite-label"
              value={label}
              placeholder={t('owner_labelPlaceholder')}
              onChange={(e) => setLabel(e.target.value)}
              autoFocus
              data-testid="voice-invite-label"
            />
          </div>
          <Button onClick={create} disabled={busy || label.trim().length < 2} data-testid="voice-invite-create">
            {t('owner_create')}
          </Button>
          <Button
            variant="ghost"
            onClick={() => { setAsking(false); setLabel(''); setError(null); }}
            data-testid="voice-invite-close"
          >
            {t('owner_close')}
          </Button>
        </div>
      )}

      {error && <p className="text-sm text-destructive">{error}</p>}

      {invites.length > 0 && (
        <ul className="space-y-2">
          {invites.map((invite) => (
            <li
              key={invite.token}
              className="flex items-center justify-between gap-2 rounded-lg border p-2 text-sm"
              data-testid={`voice-invite-row-${invite.token}`}
            >
              <div className="min-w-0">
                <p className="truncate font-medium">{invite.label}</p>
                <p className="text-xs text-muted-foreground">{t('owner_waiting')}</p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <Button size="sm" variant="outline" onClick={() => copy(invite)}>
                  {copied === invite.token ? <Check className="size-4" /> : <Copy className="size-4" />}
                  <span className="ml-1 hidden sm:inline">
                    {copied === invite.token ? t('owner_copied') : t('owner_copy')}
                  </span>
                </Button>
                <Button size="sm" variant="ghost" onClick={() => cancel(invite.token)} aria-label={t('owner_cancel')}>
                  <X className="size-4" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
